/**
 * Ni² Demo Store — an in-memory writable layer that lives alongside
 * the immutable workbook-derived seed data.
 *
 * All comments, pins and simulated uploads created during a demo run
 * flow through this store. Workbook JSON is never mutated. Every
 * entity created here carries a `demo: true` flag and a persistent
 * DEMO-* id so downstream consumers can distinguish canonical
 * workbook records from session-only demo records.
 */
import { createContext, useContext, useMemo, useState, useCallback } from "react";

const Ni2DemoStoreContext = createContext(null);

/* ---- id helpers ---------------------------------------------------- */
let __pinCounter = 0;
let __commentCounter = 0;
let __uploadCounter = 0;
const nowIso = () => new Date().toISOString();
const pinId = () => `DEMO-PIN-${String(++__pinCounter).padStart(4, "0")}`;
const commentId = () => `DEMO-COLLAB-${String(++__commentCounter).padStart(4, "0")}`;
const uploadId = () => `DEMO-FILE-${String(++__uploadCounter).padStart(4, "0")}`;

/* ---- provider ------------------------------------------------------ */
export function Ni2DemoStoreProvider({ children }) {
  // Local writable arrays. Empty at boot — the workbook seed data is
  // read through the repository separately.
  const [pins, setPins] = useState([]);              // {pinId, imagingId, caseId, x, y, createdAt, demo}
  const [comments, setComments] = useState([]);      // Collaboration-shaped records with demo:true
  const [uploads, setUploads] = useState([]);        // File placeholders "added" via the demo flow

  const createDemoPin = useCallback((input) => {
    const rec = {
      pinId: pinId(),
      imagingId: input.imagingId,
      caseId: input.caseId,
      x: input.x,     // 0..1 normalized
      y: input.y,     // 0..1 normalized
      createdAt: nowIso(),
      authorPersonId: input.authorPersonId || "ATT-001",
      demo: true,
    };
    setPins((prev) => [...prev, rec]);
    return rec;
  }, []);

  const removeDemoPin = useCallback((id) => {
    setPins((prev) => prev.filter((p) => p.pinId !== id));
    setComments((prev) => prev.filter((c) => c.pinId !== id));
  }, []);

  const createDemoImagingComment = useCallback((input) => {
    const rec = {
      collaborationId: commentId(),
      contextType: input.contextType || "Imaging",
      contextId: input.contextId,             // Imaging ID
      caseId: input.caseId,
      threadId: input.threadId || `DEMO-THREAD-${input.pinId || input.contextId}`,
      pinId: input.pinId || null,
      authorPersonId: input.authorPersonId || "ATT-001",
      mentionedPersonIds: input.mentionedPersonIds || [],
      dateAndTime: nowIso(),
      comment: input.comment,
      resolved: "No",
      resolutionSummary: "",
      demo: true,
    };
    setComments((prev) => [...prev, rec]);
    return rec;
  }, []);

  const setCommentResolved = useCallback((id, resolved) => {
    setComments((prev) =>
      prev.map((c) =>
        c.collaborationId === id
          ? { ...c, resolved: resolved ? "Yes" : "No", resolutionSummary: resolved ? "Resolved via Ni²" : "" }
          : c
      )
    );
  }, []);

  const deleteComment = useCallback((id) => {
    setComments((prev) => prev.filter((c) => c.collaborationId !== id));
  }, []);

  const createDemoFile = useCallback((input) => {
    const rec = {
      fileId: uploadId(),
      caseId: input.caseId,
      fileName: input.fileName || "Screenshot.png",
      fileType: input.fileType || "Screenshot",
      modality: input.modality || "Photo",
      studyDate: input.studyDate || nowIso().slice(0, 10),
      label: input.label || "Local screenshot",
      keyFinding: input.keyFinding || "",
      demo: true,
      placeholder: true,
    };
    setUploads((prev) => [...prev, rec]);
    return rec;
  }, []);

  const getPinsForImaging = useCallback(
    (imagingId) => pins.filter((p) => p.imagingId === imagingId),
    [pins]
  );

  const getDemoCommentsForContext = useCallback(
    (contextType, contextId) =>
      comments.filter((c) => c.contextType === contextType && c.contextId === contextId),
    [comments]
  );

  const getDemoUploadsForCase = useCallback(
    (caseId) => uploads.filter((u) => u.caseId === caseId),
    [uploads]
  );

  const value = useMemo(
    () => ({
      // reads
      pins,
      comments,
      uploads,
      getPinsForImaging,
      getDemoCommentsForContext,
      getDemoUploadsForCase,
      // writes
      createDemoPin,
      removeDemoPin,
      createDemoImagingComment,
      setCommentResolved,
      deleteComment,
      createDemoFile,
    }),
    [
      pins,
      comments,
      uploads,
      getPinsForImaging,
      getDemoCommentsForContext,
      getDemoUploadsForCase,
      createDemoPin,
      removeDemoPin,
      createDemoImagingComment,
      setCommentResolved,
      deleteComment,
      createDemoFile,
    ]
  );

  return (
    <Ni2DemoStoreContext.Provider value={value}>{children}</Ni2DemoStoreContext.Provider>
  );
}

export function useNi2DemoStore() {
  const ctx = useContext(Ni2DemoStoreContext);
  if (!ctx) {
    // Non-fatal — return a no-op store so components outside the
    // provider keep working with an empty demo layer.
    return {
      pins: [],
      comments: [],
      uploads: [],
      getPinsForImaging: () => [],
      getDemoCommentsForContext: () => [],
      getDemoUploadsForCase: () => [],
      createDemoPin: () => null,
      removeDemoPin: () => {},
      createDemoImagingComment: () => null,
      setCommentResolved: () => {},
      deleteComment: () => {},
      createDemoFile: () => null,
    };
  }
  return ctx;
}
